import { Box } from '@chakra-ui/react'
import { useEffect } from 'react'

import type { SeriesRegistrationModalData } from '@/common/services/seriesPage/types'
import isTypedObjectKey from '@/common/utils/typescript/isTypedObjectKey'

import { trackEvent } from '@/client/services/analytics'
import { useSeriesRegistrationWizardSteps } from '@/client/services/formInputs'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import isSATBootcampTemplateSlug from '@/client/services/satBootcamp/isSATBootcampTemplateSlug'
import { SERIES_RESERVATIONS_MAX } from '@/client/services/series/constants'
import { getSeriesUrl } from '@/client/services/url'

import Alert from '@/components/UI/Alert'
import FormWizardV2 from '@/components/UI/FormWizardV2/FormWizardV2'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function SeriesRegistrationModal({
  open,
  onClose,
  series,
}: {
  open: boolean
  onClose: () => void
  series: SeriesRegistrationModalData
}): JSX.Element {
  const seriesId = series.id
  const seriesQuestionnaire = series.questionnaire
  const attendancePolicy = `${series.attendancePolicy}`
  const researchGroup = series.researchGroup
  const hasRequiredAttendanceSessions = series.hasRequiredAttendanceSessions
  // We do not want to provide an infinite number of reservations, so if you previously
  // invited friends and then cancelled your reservations.
  const countUserReservationsInSeries = series.viewer.reservedForOthersCount

  const isRequiredPhoneNumberSeries = series.isRequiredPhoneNumberSeries

  const seriesTemplateSlug = series.seriesTemplateSlug
  const isSATBootcampCourse = isSATBootcampTemplateSlug(seriesTemplateSlug)

  const maxLearners = series.maxLearners ?? 0
  const spotsAvailableToReserve = Math.min(
    SERIES_RESERVATIONS_MAX,
    Math.max(0, maxLearners - series.registrationCount - series.activeReservationCount - 1)
    // ^ -1 because current user will also take one spot
  )

  const tutorName = series.tutorDisplayName ?? ''
  const tutorAvatarUrl = series.tutorAvatarUrl ?? ''

  const worldSlug = series.world?.slug ?? ''
  const programSlug = isTypedObjectKey(worldSlug, PROGRAM_WORLD_TO_PROGRAM_SLUG)
    ? PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug]
    : undefined

  const { content, handleSubmit, steps, blockedError } = useSeriesRegistrationWizardSteps({
    seriesId,
    attendancePolicy,
    seriesQuestionnaire,
    hasRequiredAttendanceSessions,
    programSlug,
    templateSlug: seriesTemplateSlug,
    tutorInfo: { name: tutorName, avatarUrl: tutorAvatarUrl },
    spotsAvailableToReserve,
    countUserReservationsInSeries,
    requirePhoneNumber: isRequiredPhoneNumberSeries ?? false,
    onClose,
    redirectUrl: getSeriesUrl(seriesId),
    HeaderComponent: () => null,
    source: 'series-page',
    researchGroup: researchGroup ?? null,
  })

  useEffect(() => {
    trackEvent({
      category: 'SAT Bootcamp - Registration',
      action: 'seen - SeriesRegistrationModal',
    })
  }, [])

  return (
    <>
      {content}
      <ModalV3
        disableClickOutside
        open={open}
        onClose={onClose}
        size="3xl"
        title={() => <Box height="4px" />}
        body={() => (
          <Box>
            {blockedError == null ? null : (
              <Alert variant="error" mb="24px">
                {blockedError}
              </Alert>
            )}
            <FormWizardV2
              enableAnalyticsTracking
              name={
                isSATBootcampCourse ? 'SATSeriesRegistrationWizard' : 'SeriesRegistrationWizard'
              }
              steps={steps}
              onSubmit={handleSubmit}
              checklistType="horizontal-stepper"
              maxWidth="100%"
              excludeNavbarPadding
            />
          </Box>
        )}
      />
    </>
  )
}
